import type { Capability } from '../features/capabilities';
import { isDefaultLabelAppearance, type BasemapLabelAppearance } from './labelAppearance';
import { basemapLabelRecipe, type BasemapLabelPresentationId } from './labelPresentation';

export type BasemapPresetId = 'none' | 'dropmap-light' | 'dropmap-streets' | 'dropmap-dark';
export type CuratedBasemapPresetId = Exclude<BasemapPresetId, 'none'>;

/** Kept as an alias while older domain call sites migrate from the original None-only seam. */
export type BasemapId = BasemapPresetId;
export type BasemapProviderId = 'none' | 'openfreemap';
export type BasemapSourceType = 'none' | 'vector';
export type BasemapSemanticGroup =
  | 'citiesTowns'
  | 'suburbsLocalities'
  | 'roadNames'
  | 'routeShields'
  | 'transitLabels'
  | 'naturalWaterLabels'
  | 'otherLabels'
  | 'roads'
  | 'water'
  | 'parksGreenSpace'
  | 'naturalVegetation'
  | 'buildings'
  | 'countryBoundaries'
  | 'regionalBoundaries'
  | 'localBoundaries'
  | 'pointsOfInterest'
  | 'transitLines';

export type BasemapColourGroup =
  | 'water'
  | 'motorwaysHighways'
  | 'arterialRoads'
  | 'collectorRoads'
  | 'localRoads'
  | 'parksGreenSpace'
  | 'naturalVegetation';

export const BASEMAP_SEMANTIC_GROUPS: readonly BasemapSemanticGroup[] = [
  'citiesTowns',
  'suburbsLocalities',
  'roadNames',
  'routeShields',
  'transitLabels',
  'naturalWaterLabels',
  'otherLabels',
  'roads',
  'buildings',
  'water',
  'parksGreenSpace',
  'naturalVegetation',
  'countryBoundaries',
  'regionalBoundaries',
  'localBoundaries',
  'pointsOfInterest',
  'transitLines',
];

export const BASEMAP_COLOUR_GROUPS: readonly BasemapColourGroup[] = [
  'water',
  'motorwaysHighways',
  'arterialRoads',
  'collectorRoads',
  'localRoads',
  'parksGreenSpace',
  'naturalVegetation',
];

export const CURATED_BASEMAP_PRESET_IDS: readonly CuratedBasemapPresetId[] = [
  'dropmap-light',
  'dropmap-streets',
  'dropmap-dark',
];

export interface BasemapCustomization {
  readonly labelAppearance?: BasemapLabelAppearance;
  readonly contextOpacity: number | null;
  readonly labelsOpacity: number | null;
  readonly visibility: Readonly<Record<BasemapSemanticGroup, boolean | null>>;
  readonly colours: Readonly<Record<BasemapColourGroup, string | null>>;
}

export interface EffectiveBasemapCustomization {
  readonly contextOpacity: number;
  readonly labelsOpacity: number;
  readonly visibility: Readonly<Record<BasemapSemanticGroup, boolean>>;
  readonly colours: Readonly<Record<BasemapColourGroup, string>>;
}

export interface BasemapConfiguration {
  readonly presetId: BasemapPresetId;
  readonly buildings3d: boolean;
  /** Omitted pre-11R projects follow their saved basemap values without rewriting them. */
  readonly labelPresentation?: BasemapLabelPresentationId;
  readonly customizationByPreset: Readonly<Record<CuratedBasemapPresetId, BasemapCustomization>>;
}

export interface AttributionCredit {
  /** Stable identity used to deduplicate obligations shared by visible sources. */
  readonly id: string;
  readonly required: boolean;
  readonly interactive: { readonly label: string; readonly url: string };
  readonly static: { readonly label: string; readonly urlText?: string };
}

export interface BasemapAttribution {
  readonly credits: readonly AttributionCredit[];
  readonly providerBrandingRequired: boolean;
  readonly logoRequirement: 'none' | 'required';
}

export interface BasemapRuntimeContext {
  /** Future providers can resolve secrets here without placing them in project state. */
  readonly credential?: (providerId: BasemapProviderId) => string | undefined;
}

export interface BasemapProviderDefinition {
  readonly id: BasemapProviderId;
  readonly label: string;
  readonly sourceType: BasemapSourceType;
  readonly supportsVectorStyling: boolean;
  readonly supports3dBuildings: boolean;
  readonly staticExportCompatible: boolean;
  readonly credentialRequirement: 'none' | 'runtime';
  readonly requiredCapability?: Capability;
  readonly attribution?: BasemapAttribution;
  readonly semanticSourceLayers: Readonly<Record<BasemapSemanticGroup, readonly string[]>>;
  readonly resolveStyleUrl?: (providerStyleId: string, context: BasemapRuntimeContext) => string;
}

export interface BasemapPresetDefinition {
  readonly id: BasemapPresetId;
  readonly label: string;
  readonly description: string;
  readonly providerId: BasemapProviderId;
  /** Code-owned provider detail. This is never persisted to a project. */
  readonly providerStyleId?: 'positron' | 'liberty' | 'dark';
  readonly theme: 'neutral' | 'light' | 'dark';
  readonly requiresNetwork: boolean;
  readonly customizationDefaults: EffectiveBasemapCustomization;
  readonly supportedSemanticGroups: readonly BasemapSemanticGroup[];
}

const ALL_SEMANTIC_GROUPS = BASEMAP_SEMANTIC_GROUPS;
const WITHOUT_POI_OR_TRANSIT_LABELS = BASEMAP_SEMANTIC_GROUPS.filter(
  (group) => group !== 'pointsOfInterest' && group !== 'transitLabels',
);

const EMPTY_SEMANTIC_GROUPS: Readonly<Record<BasemapSemanticGroup, readonly string[]>> = {
  citiesTowns: [],
  suburbsLocalities: [],
  roadNames: [],
  routeShields: [],
  transitLabels: [],
  naturalWaterLabels: [],
  otherLabels: [],
  roads: [],
  water: [],
  parksGreenSpace: [],
  naturalVegetation: [],
  buildings: [],
  countryBoundaries: [],
  regionalBoundaries: [],
  localBoundaries: [],
  pointsOfInterest: [],
  transitLines: [],
};

export const OPENFREEMAP_ATTRIBUTION: BasemapAttribution = {
  providerBrandingRequired: false,
  logoRequirement: 'none',
  credits: [
    {
      id: 'openmaptiles-schema',
      required: true,
      interactive: { label: '© OpenMapTiles', url: 'https://openmaptiles.org/' },
      static: { label: '© OpenMapTiles' },
    },
    {
      id: 'openstreetmap-data',
      required: true,
      interactive: {
        label: '© OpenStreetMap',
        url: 'https://www.openstreetmap.org/copyright',
      },
      static: { label: '© OpenStreetMap', urlText: 'openstreetmap.org/copyright' },
    },
  ],
};

export const BASEMAP_PROVIDERS: Readonly<Record<BasemapProviderId, BasemapProviderDefinition>> = {
  none: {
    id: 'none',
    label: 'None',
    sourceType: 'none',
    supportsVectorStyling: false,
    supports3dBuildings: false,
    staticExportCompatible: true,
    credentialRequirement: 'none',
    semanticSourceLayers: EMPTY_SEMANTIC_GROUPS,
  },
  openfreemap: {
    id: 'openfreemap',
    label: 'OpenFreeMap',
    sourceType: 'vector',
    supportsVectorStyling: true,
    supports3dBuildings: true,
    staticExportCompatible: true,
    credentialRequirement: 'none',
    attribution: OPENFREEMAP_ATTRIBUTION,
    semanticSourceLayers: {
      citiesTowns: ['place'],
      suburbsLocalities: ['place'],
      roadNames: ['transportation_name'],
      routeShields: ['transportation_name'],
      transitLabels: ['poi'],
      naturalWaterLabels: ['water_name', 'waterway'],
      otherLabels: ['aerodrome_label', 'housenumber', 'mountain_peak', 'place'],
      roads: ['transportation'],
      water: ['water', 'water_name', 'waterway'],
      parksGreenSpace: ['landuse', 'park'],
      naturalVegetation: ['landcover'],
      buildings: ['building', 'housenumber'],
      countryBoundaries: ['boundary'],
      regionalBoundaries: ['boundary'],
      localBoundaries: ['boundary'],
      pointsOfInterest: ['poi'],
      transitLines: ['transportation'],
    },
    resolveStyleUrl: (providerStyleId) => `https://tiles.openfreemap.org/styles/${providerStyleId}`,
  },
};

const visibleByDefault: Readonly<Record<BasemapSemanticGroup, boolean>> = {
  citiesTowns: true,
  suburbsLocalities: true,
  roadNames: true,
  routeShields: true,
  transitLabels: true,
  naturalWaterLabels: true,
  otherLabels: true,
  roads: true,
  buildings: true,
  water: true,
  parksGreenSpace: true,
  naturalVegetation: true,
  countryBoundaries: true,
  regionalBoundaries: true,
  localBoundaries: false,
  pointsOfInterest: true,
  transitLines: true,
};

function presetDefaults(
  colours: EffectiveBasemapCustomization['colours'],
  visibility: Partial<EffectiveBasemapCustomization['visibility']> = {},
): EffectiveBasemapCustomization {
  return {
    contextOpacity: 1,
    labelsOpacity: 1,
    visibility: { ...visibleByDefault, ...visibility },
    colours,
  };
}

export const BASEMAP_CATALOG: Readonly<Record<BasemapPresetId, BasemapPresetDefinition>> = {
  none: {
    id: 'none',
    label: 'None',
    description: 'No reference map',
    providerId: 'none',
    theme: 'neutral',
    requiresNetwork: false,
    supportedSemanticGroups: [],
    customizationDefaults: presetDefaults({
      water: '#cddfe5',
      motorwaysHighways: '#d7dadd',
      arterialRoads: '#e4e6e8',
      collectorRoads: '#eceeef',
      localRoads: '#ffffff',
      parksGreenSpace: '#e6e9e5',
      naturalVegetation: '#dce0dc',
    }),
  },
  'dropmap-light': {
    id: 'dropmap-light',
    label: 'Placeio Maps Light',
    description: 'Clear, quiet reference map',
    providerId: 'openfreemap',
    providerStyleId: 'positron',
    theme: 'light',
    requiresNetwork: true,
    supportedSemanticGroups: WITHOUT_POI_OR_TRANSIT_LABELS,
    customizationDefaults: presetDefaults(
      {
        water: '#c5d3d8',
        motorwaysHighways: '#d4d8d6',
        arterialRoads: '#e3e6e1',
        collectorRoads: '#eceee9',
        localRoads: '#e5e7e2',
        parksGreenSpace: '#e6e9e5',
        naturalVegetation: '#dce0dc',
      },
      { pointsOfInterest: false, transitLabels: false },
    ),
  },
  'dropmap-streets': {
    id: 'dropmap-streets',
    label: 'Placeio Maps Streets',
    description: 'Detailed roads and places',
    providerId: 'openfreemap',
    providerStyleId: 'liberty',
    theme: 'light',
    requiresNetwork: true,
    supportedSemanticGroups: ALL_SEMANTIC_GROUPS,
    customizationDefaults: presetDefaults(
      {
        water: '#dee6eb',
        motorwaysHighways: '#e9c99b',
        arterialRoads: '#ede1b9',
        collectorRoads: '#e8e8e8',
        localRoads: '#ffffff',
        parksGreenSpace: '#d8e8c8',
        naturalVegetation: '#b0d59a',
      },
      { buildings: false },
    ),
  },
  'dropmap-dark': {
    id: 'dropmap-dark',
    label: 'Placeio Maps Dark',
    description: 'Dark reference map',
    providerId: 'openfreemap',
    providerStyleId: 'dark',
    theme: 'dark',
    requiresNetwork: true,
    supportedSemanticGroups: WITHOUT_POI_OR_TRANSIT_LABELS,
    customizationDefaults: presetDefaults(
      {
        water: '#17232e',
        motorwaysHighways: '#59616a',
        arterialRoads: '#454e57',
        collectorRoads: '#343d45',
        localRoads: '#2d343b',
        parksGreenSpace: '#2e3c33',
        naturalVegetation: '#29392f',
      },
      { pointsOfInterest: false, transitLabels: false },
    ),
  },
};

export const BASEMAP_PRESETS = Object.values(BASEMAP_CATALOG);

export const DEFAULT_BASEMAP_CUSTOMIZATION: BasemapCustomization = {
  contextOpacity: null,
  labelsOpacity: null,
  visibility: {
    citiesTowns: null,
    suburbsLocalities: null,
    roadNames: null,
    routeShields: null,
    transitLabels: null,
    naturalWaterLabels: null,
    otherLabels: null,
    roads: null,
    buildings: null,
    water: null,
    parksGreenSpace: null,
    naturalVegetation: null,
    countryBoundaries: null,
    regionalBoundaries: null,
    localBoundaries: null,
    pointsOfInterest: null,
    transitLines: null,
  },
  colours: {
    water: null,
    motorwaysHighways: null,
    arterialRoads: null,
    collectorRoads: null,
    localRoads: null,
    parksGreenSpace: null,
    naturalVegetation: null,
  },
};

export const DEFAULT_BASEMAP_CUSTOMIZATION_BY_PRESET: BasemapConfiguration['customizationByPreset'] =
  {
    'dropmap-light': DEFAULT_BASEMAP_CUSTOMIZATION,
    'dropmap-streets': DEFAULT_BASEMAP_CUSTOMIZATION,
    'dropmap-dark': DEFAULT_BASEMAP_CUSTOMIZATION,
  };

export const DEFAULT_BASEMAP_CONFIGURATION: BasemapConfiguration = {
  presetId: 'none',
  buildings3d: false,
  customizationByPreset: DEFAULT_BASEMAP_CUSTOMIZATION_BY_PRESET,
};

export function basemapPreset(id: BasemapPresetId): BasemapPresetDefinition {
  return BASEMAP_CATALOG[id];
}

export function basemapProviderFor(id: BasemapPresetId): BasemapProviderDefinition {
  return BASEMAP_PROVIDERS[basemapPreset(id).providerId];
}

export function basemapSupportsSemanticGroup(
  presetId: BasemapPresetId,
  group: BasemapSemanticGroup,
): boolean {
  return basemapPreset(presetId).supportedSemanticGroups.includes(group);
}

export function basemapCustomizationFor(
  configuration: BasemapConfiguration,
  presetId: CuratedBasemapPresetId,
): BasemapCustomization {
  return configuration.customizationByPreset[presetId];
}

export function effectiveBasemapCustomization(
  configuration: BasemapConfiguration,
  presetId: CuratedBasemapPresetId,
): EffectiveBasemapCustomization {
  const values = baseEffectiveBasemapCustomization(configuration, presetId);
  const intent = configuration.labelPresentation ?? 'follow-basemap';
  const overrides = basemapCustomizationFor(configuration, presetId);
  const tiers = overrides.labelAppearance?.roadTiers;
  const roadVisibility = tiers ? { roadNames: Object.values(tiers).some(Boolean) } : {};
  if (intent === 'follow-basemap')
    return { ...values, visibility: { ...values.visibility, ...roadVisibility } };
  const labels = basemapLabelRecipe(presetId, intent);
  return {
    ...values,
    labelsOpacity: overrides.labelsOpacity ?? labels.opacity,
    visibility: {
      ...values.visibility,
      ...Object.fromEntries(
        Object.entries(labels.visibility).map(([group, visible]) => [
          group,
          overrides.visibility[group as BasemapSemanticGroup] ?? visible,
        ]),
      ),
      ...roadVisibility,
    },
  };
}

/** Resolves the saved 11Q values, independently of the selected label intent. */
export function baseEffectiveBasemapCustomization(
  configuration: BasemapConfiguration,
  presetId: CuratedBasemapPresetId,
): EffectiveBasemapCustomization {
  const defaults = basemapPreset(presetId).customizationDefaults;
  const overrides = basemapCustomizationFor(configuration, presetId);
  return {
    contextOpacity: overrides.contextOpacity ?? defaults.contextOpacity,
    labelsOpacity: overrides.labelsOpacity ?? defaults.labelsOpacity,
    visibility: Object.fromEntries(
      BASEMAP_SEMANTIC_GROUPS.map((group) => [
        group,
        overrides.visibility[group] ?? defaults.visibility[group],
      ]),
    ) as unknown as EffectiveBasemapCustomization['visibility'],
    colours: Object.fromEntries(
      BASEMAP_COLOUR_GROUPS.map((group) => [
        group,
        overrides.colours[group] ?? defaults.colours[group],
      ]),
    ) as unknown as EffectiveBasemapCustomization['colours'],
  };
}

export function isBasemapPresetCustomized(
  configuration: BasemapConfiguration,
  presetId: CuratedBasemapPresetId,
): boolean {
  const customization = basemapCustomizationFor(configuration, presetId);
  return (
    !isDefaultLabelAppearance(customization.labelAppearance) ||
    customization.contextOpacity !== null ||
    customization.labelsOpacity !== null ||
    BASEMAP_SEMANTIC_GROUPS.some((group) => customization.visibility[group] !== null) ||
    BASEMAP_COLOUR_GROUPS.some((group) => customization.colours[group] !== null)
  );
}

export function basemapAttribution(
  configuration: Pick<BasemapConfiguration, 'presetId'>,
): BasemapAttribution | undefined {
  return basemapProviderFor(configuration.presetId).attribution;
}

export function deduplicateAttributionCredits(
  credits: readonly AttributionCredit[],
): readonly AttributionCredit[] {
  const seen = new Set<string>();
  return credits.filter((credit) => {
    if (seen.has(credit.id)) return false;
    seen.add(credit.id);
    return true;
  });
}

export function interactiveBasemapAttribution(
  configuration: Pick<BasemapConfiguration, 'presetId'>,
): readonly AttributionCredit['interactive'][] {
  const attribution = basemapAttribution(configuration);
  return attribution
    ? deduplicateAttributionCredits(attribution.credits)
        .filter((credit) => credit.required)
        .map((credit) => credit.interactive)
    : [];
}

export function staticBasemapAttributionText(
  configuration: Pick<BasemapConfiguration, 'presetId'>,
): string | undefined {
  const attribution = basemapAttribution(configuration);
  if (!attribution) return undefined;
  const parts = deduplicateAttributionCredits(attribution.credits)
    .filter((credit) => credit.required)
    .flatMap((credit) => [
      credit.static.label,
      ...(credit.static.urlText ? [credit.static.urlText] : []),
    ]);
  return parts.length > 0 ? `Basemap: ${parts.join(' · ')}` : undefined;
}
